import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { cacheHistory, getCachedHistory } from '../utils/offlineStore';

const API = window.__WR_API_BASE__;
const API_BASE = `${API}/api`;

// Notification sound for team updates
const NOTIFICATION_SOUND = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleowxQUKOxM/LooI4SUOYwMi+l3g4VWKAoLSxpZZ9dXiBipKYlI6Gfn5+gYaLkJSVkpCMiIR/fXx9gISJjpGTkpCNiYR/fXx+gYaLkJSWk5GPi4Z/fHx+gYWKj5OWlZKPjIeCfnt8f4OIjZGUlZOQjYmEf3x7fYCFio+TlpWTkI2Jgn56fH+DCI2QlJaUkpCNiYR/fXt9gIWKj5OVlJKPjImEf3x7fYCEiY6SlJSTkI2JhH98e32AhYqPk5aVk5CNiYJ+enx/g4iNkZSVk5CPjImEf3x7fYCFio+TlpWTkI2JhH98e32AhYqPk5aVk5CNiYJ+ent/g4iNkZSVk5CPjImDf3x7fYCFio+TlpWTkI2Jgn56e3+DiI2RlJWTkI+MiYN/fHt9gIWKj5OWlZOQjYmCfnp7f4OIjZGUlZOQj4yJg398e32AhYqPk5aVk5CNiYJ+ent/g4iNkZSVk5CPjImDf3x7fYCFio+TlpWTkI2Jgn56e3+DiI2RlJWTkI+MiYN/fHt9gIWKj5OWlZKQjYmCfnp7f4OIjZGUlZOQj4yJg398e32AhYqPk5aVk5CNiYJ+ent/g4iNkZSVk5CPjImDf3x7fYCFio+TlpWTkI2Jgn56e3+DiI2RlJWTkI+MiYN/fHt9gA==';

/**
 * Custom hook for managing generation history
 * Handles loading, filtering, grouping, batch operations, and history sharing
 */
export const useHistory = (user, effectiveRole, guestId = null) => {
  // Core history state
  const [showHistory, setShowHistory] = useState(false); // Collapsed by default
  const [history, setHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);
  
  // Batch selection state
  const [batchSelectedItems, setBatchSelectedItems] = useState(new Set());
  const [isBatchMode, setIsBatchMode] = useState(false);
  
  // Filter and grouping state
  const [historyFilter, setHistoryFilter] = useState('all'); // 'all', 'today', 'week'
  const [historySearchQuery, setHistorySearchQuery] = useState(''); // Search query
  const [condenseVersions, setCondenseVersions] = useState(true); // Auto-collapse versions
  const [expandedGroups, setExpandedGroups] = useState(new Set()); // Which groups are expanded
  const [dateJump, setDateJump] = useState(''); // Date-jump filter (YYYY-MM-DD); supersedes today/week
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' (newest first) | 'asc' (oldest first)
  
  // Special views
  const [showWeekendCatchup, setShowWeekendCatchup] = useState(false);
  const [showOrdersReview, setShowOrdersReview] = useState(false);
  
  // Sharing state
  const [historyShareCode, setHistoryShareCode] = useState(null);
  
  // Team notes notification state
  const [lastTeamNotesCount, setLastTeamNotesCount] = useState(0);
  const [teamNotificationsEnabled, setTeamNotificationsEnabled] = useState(true);
  const audioRef = useRef(null);
  
  // Initialize notification audio
  useEffect(() => {
    audioRef.current = new Audio(NOTIFICATION_SOUND);
    audioRef.current.volume = 0.3;
  }, []);
  
  // Play notification for new team notes
  const playTeamNotification = useCallback(() => {
    if (!teamNotificationsEnabled) return;
    try {
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(() => {});
      }
      if ('vibrate' in navigator) {
        navigator.vibrate([100, 50, 100]);
      }
    } catch (e) {
      console.log('Notification failed:', e);
    }
  }, [teamNotificationsEnabled]);

  // Load history from API
  const loadHistory = useCallback(async () => {
    // Allow loading for authenticated users OR guests with a guestId
    if (!user && !guestId) {
      // Not signed in and no guest ID - don't try to load history
      setHistory([]);
      return;
    }
    setIsLoadingHistory(true);
    try {
      // Add guest_id param if not authenticated
      const response = await axios.get(`${API_BASE}/history?limit=600`, { withCredentials: true });
      const newHistory = response.data || [];
      
      // Check for new team notes and notify
      const newTeamNotesCount = newHistory.reduce((sum, entry) => {
        return sum + (entry.team_notes?.length || 0);
      }, 0);
      
      if (newTeamNotesCount > lastTeamNotesCount && lastTeamNotesCount > 0) {
        playTeamNotification();
        // Find the newest note to show in toast
        const entriesWithNotes = newHistory.filter(e => e.team_notes?.length > 0);
        if (entriesWithNotes.length > 0) {
          const latestEntry = entriesWithNotes[0];
          const latestNote = latestEntry.team_notes[latestEntry.team_notes.length - 1];
          toast.info(`Team note from ${latestNote.author}: "${latestNote.note.substring(0, 50)}..."`, {
            duration: 5000
          });
        }
      }
      setLastTeamNotesCount(newTeamNotesCount);
      
      setHistory(newHistory);
      
      // Cache to IndexedDB for offline access (last 50 entries; spec target)
      // Best-effort — failures are swallowed inside `cacheHistory`. Replaces
      // the prior lossy localStorage cache, which dropped fields needed for
      // full history reload (deferred topics, PE exceptions, visit/encounter
      // types). Feb 16, 2026 — Lean Offline Mode MVP.
      cacheHistory(newHistory);
      // Keep the legacy localStorage mirror as a fallback only for the very
      // first paint when IndexedDB isn't ready yet. Trimmed to 25 to keep
      // quota-safe.
      try {
        const offlineCache = newHistory.slice(0, 25).map(entry => ({
          id: entry.id,
          clinical_note: entry.clinical_note,
          admin_letter: entry.admin_letter,
          icd10_suggestions: entry.icd10_suggestions,
          chief_complaint: entry.chief_complaint,
          patient_age: entry.patient_age,
          patient_gender: entry.patient_gender,
          patient_status: entry.patient_status,
          encounter_type: entry.encounter_type,
          note_title: entry.note_title,
          timestamp: entry.timestamp || entry.created_at,
          referrals: entry.referrals,
          imaging_orders: entry.imaging_orders,
        }));
        localStorage.setItem('writeright_offline_history', JSON.stringify(offlineCache));
      } catch (e) {
        console.warn('Offline history (localStorage mirror) cache failed:', e);
      }
    } catch (error) {
      console.error('Failed to load history:', error);
      // Don't show error toast if just not authenticated
      if (error.response?.status !== 401) {
        // Try loading from IndexedDB first (richer cache, last 50 entries)
        try {
          const idbCached = await getCachedHistory();
          if (idbCached && idbCached.length > 0) {
            setHistory(idbCached);
            toast.warning(`Offline mode: showing ${idbCached.length} cached notes`, { duration: 5000 });
            return;
          }
        } catch (e) {
          console.warn('IDB history cache read failed:', e);
        }
        // Fallback to legacy localStorage mirror
        try {
          const cached = JSON.parse(localStorage.getItem('writeright_offline_history') || '[]');
          if (cached.length > 0) {
            setHistory(cached);
            toast.warning(`Offline mode: showing ${cached.length} cached notes`, { duration: 5000 });
          } else {
            toast.error('Failed to load history — no offline cache available');
            setHistory([]);
          }
        } catch (e) {
          toast.error('Failed to load history');
          setHistory([]);
        }
      } else {
        setHistory([]);
      }
    } finally {
      setIsLoadingHistory(false);
    }
  }, [user, guestId, lastTeamNotesCount, playTeamNotification]);

  // Load history when showHistory changes or user logs in
  useEffect(() => {
    if (showHistory) loadHistory();
  }, [showHistory, user, guestId, loadHistory]);

  // Also load history count on initial mount (for badge to show correct count)
  useEffect(() => {
    if ((user || guestId) && effectiveRole === 'provider') {
      loadHistory();
    }
  }, [user, guestId, effectiveRole, loadHistory]);

  // Filter history by date and search query
  const getFilteredHistory = useCallback(() => {
    let filtered = history;
    
    // Apply date filter
    if (historyFilter !== 'all') {
      const now = new Date();
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const startOfWeek = new Date(startOfToday);
      startOfWeek.setDate(startOfWeek.getDate() - 7);
      
      filtered = history.filter(item => {
        const itemDate = new Date(item.timestamp);
        if (historyFilter === 'today') {
          return itemDate >= startOfToday;
        } else if (historyFilter === 'week') {
          return itemDate >= startOfWeek;
        } else if (historyFilter === 'letters') {
          return !!(item.admin_letter || item.result?.admin_letter);
        }
        return true;
      });
    }
    
    // NOTE (May 5, 2026): `dateJump` no longer FILTERS the list. Instead it
    // is consumed by HistoryPanel / HistorySlidePanel to scroll the list to
    // the first item matching that date and briefly highlight matching rows.
    // Filtering by date hid surrounding context and left users scrolled out
    // of view when no items existed for that exact day.

    // Apply search filter
    if (historySearchQuery.trim()) {
      const query = historySearchQuery.toLowerCase().trim();
      filtered = filtered.filter(item => {
        // Search in original notes
        if (item.original_notes?.toLowerCase().includes(query)) return true;
        // Search in chief complaint
        if (item.chief_complaint?.toLowerCase().includes(query)) return true;
        // Search in clinical note output
        if (item.clinical_note?.toLowerCase().includes(query)) return true;
        if (item.result?.clinical_note?.toLowerCase().includes(query)) return true;
        if (item.result?.soap_note?.toLowerCase().includes(query)) return true;
        // Search in admin letter output
        if (item.result?.admin_letter?.toLowerCase().includes(query)) return true;
        // Search in letter type
        if (item.letter_type?.toLowerCase().includes(query)) return true;
        // Search in ICD-10 codes
        if (item.icd10_suggestions?.some(code => {
          const codeStr = typeof code === 'object' ? `${code.code} ${code.description}` : String(code);
          return codeStr.toLowerCase().includes(query);
        })) return true;
        // Search in imaging orders
        if (item.imaging_orders?.some(order => 
          `${order.imaging_type || ''} ${order.body_part || ''} ${order.indication || ''}`.toLowerCase().includes(query)
        )) return true;
        // Search in referrals
        if (item.referrals?.some(ref => 
          `${ref.specialty || ''} ${ref.indication || ''}`.toLowerCase().includes(query)
        )) return true;
        return false;
      });
    }
    
    // If condensing versions, group entries by similar content.
    // Key = date + patient signature (age+gender) + normalized content fingerprint.
    // Fingerprint strips ICD-10 / CPT code patterns so rearranging diagnosis
    // codes (which doesn't change the underlying encounter) collapses into
    // the same group. Fixes "rearrange ICDs creates a phantom duplicate
    // version" bug reported May 5, 2026 — applies retroactively to all
    // historical entries since grouping is computed client-side on every load.
    if (condenseVersions) {
      const groups = new Map();

      // Strip ICD-10 code patterns ([A-Z]\d{2}(\.\d{1,4})?) and 5-digit CPTs
      // before fingerprinting so reordering those codes in the dictation /
      // chief complaint does NOT change the grouping key.
      const _stripCodes = (s) => (s || '')
        .replace(/\b[A-Za-z]\d{1,2}(?:\.\d{1,4})?\b/g, '')   // ICD-10
        .replace(/\b\d{5}\b/g, '');                            // CPT
      const _norm = (s) => _stripCodes(s).toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 120);

      filtered.forEach(item => {
        const itemDate = new Date(item.timestamp);
        const dateKey = `${itemDate.getFullYear()}-${itemDate.getMonth()}-${itemDate.getDate()}`;
        // Patient signature — age + gender. Keeps different patients on the
        // same day separate even if their CC text happens to look similar.
        const patientSig = `${item.patient_age || '?'}-${(item.patient_gender || '?').toLowerCase()}`;
        const contentKey = _norm(
          item.original_notes ||
          item.notes ||
          item.chief_complaint ||
          item.clinical_note ||
          item.id
        );
        const groupKey = `${dateKey}_${patientSig}_${contentKey}`;
        
        if (!groups.has(groupKey)) {
          groups.set(groupKey, {
            key: groupKey,
            items: [],
            latestItem: item,
            latestTimestamp: new Date(item.timestamp)
          });
        }
        
        const group = groups.get(groupKey);
        group.items.push(item);
        
        // Keep track of the latest version
        const itemTime = new Date(item.timestamp);
        if (itemTime > group.latestTimestamp) {
          group.latestTimestamp = itemTime;
          group.latestItem = item;
        }
      });
      
      // Convert groups to array with version count
      const result = [];
      groups.forEach(group => {
        if (group.items.length === 1) {
          // Single item, no grouping needed
          result.push({ ...group.latestItem, _versionCount: 1, _groupKey: group.key, _allVersions: group.items });
        } else {
          // Multiple versions - show latest with count
          result.push({ 
            ...group.latestItem, 
            _versionCount: group.items.length, 
            _groupKey: group.key,
            _allVersions: group.items.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
          });
        }
      });
      
      // Sort by latest timestamp
      return result.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    }
    
    // Apply sort order — affects flat list ordering. Day grouping in panel
    // components will respect this order naturally since they iterate in array
    // order. 'desc' = newest first (default), 'asc' = oldest first.
    filtered = [...filtered].sort((a, b) => {
      const tA = new Date(a.timestamp || a.created_at).getTime();
      const tB = new Date(b.timestamp || b.created_at).getTime();
      return sortOrder === 'asc' ? tA - tB : tB - tA;
    });

    return filtered;
  }, [history, historyFilter, historySearchQuery, condenseVersions, sortOrder]);

  // Batch selection handlers
  const toggleBatchSelect = useCallback((itemId, e) => {
    e.stopPropagation();
    setBatchSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  }, []);

  const selectAllHistory = useCallback(() => {
    const filtered = getFilteredHistory();
    if (batchSelectedItems.size === filtered.length) {
      setBatchSelectedItems(new Set());
    } else {
      setBatchSelectedItems(new Set(filtered.map(item => item.id)));
    }
  }, [getFilteredHistory, batchSelectedItems.size]);

  const exitBatchMode = useCallback(() => {
    setIsBatchMode(false);
    setBatchSelectedItems(new Set());
  }, []);

  // Batch copy notes and ICD-10s
  const batchCopyNotesAndICD10s = useCallback(async () => {
    if (batchSelectedItems.size === 0) {
      toast.warning('No items selected');
      return;
    }

    const selectedItems = history.filter(item => batchSelectedItems.has(item.id));
    let combinedText = '';
    
    selectedItems.forEach((item, index) => {
      const timestamp = new Date(item.timestamp).toLocaleString();
      combinedText += `${'='.repeat(60)}\n`;
      combinedText += `ENCOUNTER ${index + 1} - ${timestamp}\n`;
      combinedText += `${'='.repeat(60)}\n\n`;
      
      // Add Clinical Note
      if (item.clinical_note) {
        combinedText += `CLINICAL NOTE:\n${'-'.repeat(40)}\n`;
        combinedText += `${item.clinical_note}\n\n`;
      }
      
      // Add ICD-10 Codes
      if (item.icd10_suggestions && item.icd10_suggestions.length > 0) {
        combinedText += `ICD-10 CODES:\n${'-'.repeat(40)}\n`;
        item.icd10_suggestions.forEach(code => {
          if (typeof code === 'object') {
            combinedText += `${code.code} - ${code.description}\n`;
          } else {
            combinedText += `${code}\n`;
          }
        });
        combinedText += '\n';
      }
      
      combinedText += '\n';
    });

    try {
      await navigator.clipboard.writeText(combinedText);
      toast.success(`Copied ${selectedItems.length} encounter${selectedItems.length > 1 ? 's' : ''} to clipboard!`, {
        description: 'Clinical notes + ICD-10 codes'
      });
      exitBatchMode();
    } catch (err) {
      toast.error('Failed to copy to clipboard');
    }
  }, [batchSelectedItems, history, exitBatchMode]);

  // Toggle group expansion
  const toggleGroupExpanded = useCallback((groupKey) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupKey)) {
        newSet.delete(groupKey);
      } else {
        newSet.add(groupKey);
      }
      return newSet;
    });
  }, []);

  // Share today's history with medic
  const startShareHistory = useCallback(async (durationHours = 72) => {
    try {
      // Include guest_id if not authenticated
      const payload = { duration_hours: durationHours };
      if (guestId) {
        payload.guest_id = guestId;
      }
      const response = await axios.post(`${API_BASE}/session/share-history`, payload, { withCredentials: true });
      setHistoryShareCode(response.data.share_code);
      const shareUrl = `${window.location.origin}/history/${response.data.share_code}`;
      // Try to copy, but don't crash if it fails
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(shareUrl);
        }
      } catch (clipErr) {
        console.warn('Clipboard copy failed:', clipErr);
      }
      const durationLabel = response.data.duration_label || `${durationHours} hours`;
      toast.success(`History share link created! ${durationLabel}`, { duration: 5000 });
    } catch (error) {
      toast.error('Failed to create history share link');
    }
  }, [guestId]);

  // Get pending proposals count (awaiting medic completion)
  const getPendingProposals = useCallback(() => {
    const pendingMeds = history.filter(h => h.proposed_medications?.some(m => !m.completed)).length;
    const pendingProfiles = history.filter(h => h.proposed_profile && !h.proposed_profile.completed).length;
    return {
      pendingMeds,
      pendingProfiles,
      total: pendingMeds + pendingProfiles
    };
  }, [history]);

  // Get items ready for provider signature (completed by medic)
  const getReadyForSignature = useCallback(() => {
    // Count medications that are marked as completed by medic
    let medsReady = 0;
    let profilesReady = 0;
    
    history.forEach(h => {
      // Count completed medications (ready for provider to sign off)
      if (h.proposed_medications?.some(m => m.completed && !m.signed)) {
        medsReady += h.proposed_medications.filter(m => m.completed && !m.signed).length;
      }
      // Count completed profiles (ready for provider to sign off)
      if (h.proposed_profile?.completed && !h.proposed_profile.signed) {
        profilesReady++;
      }
    });
    
    return {
      meds: medsReady,
      profiles: profilesReady,
      total: medsReady + profilesReady
    };
  }, [history]);

  // Get count of pending drafts (notes without generated docs, not already submitted to Genesis)
  const getPendingDraftsCount = useCallback(() => {
    return history.filter(h => {
      // If already submitted to Genesis, it's done — not pending
      if (h.completed === true || h.completed_and_submitted === true) return false;
      // Check if it's explicitly marked as draft
      if (h.is_draft === true) return true;
      // Or if it has notes but no generated clinical note
      const hasNotes = h.original_notes && h.original_notes.trim().length > 10;
      const hasGeneratedNote = h.clinical_note || h.result?.clinical_note || h.result?.soap_note;
      return hasNotes && !hasGeneratedNote;
    }).length;
  }, [history]);

  // Count notes that are generated but NOT yet submitted to Genesis — the real action queue
  const getUnsubmittedCount = useCallback(() => {
    return history.filter(h => {
      if (h.completed === true || h.completed_and_submitted === true) return false;
      if (h.is_draft === true) return false; // drafts are in getPendingDraftsCount already
      return !!(h.clinical_note || h.admin_letter || h.indication);
    }).length;
  }, [history]);

  // Count notes actually submitted to MHS Genesis (the "real" completed count)
  const getSubmittedCount = useCallback(() => {
    return history.filter(h => h.completed === true || h.completed_and_submitted === true).length;
  }, [history]);

  // Delete a single history item
  const deleteHistoryItem = useCallback(async (itemId) => {
    try {
      await axios.delete(`${API_BASE}/history/${itemId}`, { withCredentials: true });
      setHistory(prev => prev.filter(h => h.id !== itemId));
      toast.success('Item deleted');
      return true;
    } catch (error) {
      console.error('Failed to delete item:', error);
      toast.error('Failed to delete item');
      return false;
    }
  }, []);

  // Batch delete selected items
  const batchDeleteItems = useCallback(async () => {
    if (batchSelectedItems.size === 0) return;
    
    const itemIds = Array.from(batchSelectedItems);
    const toastId = toast.loading(`Deleting ${itemIds.length} items...`);
    
    try {
      // Delete items one by one (could be optimized with bulk endpoint)
      let successCount = 0;
      for (const itemId of itemIds) {
        try {
          await axios.delete(`${API_BASE}/history/${itemId}`, { withCredentials: true });
          successCount++;
        } catch (e) {
          console.error(`Failed to delete ${itemId}:`, e);
        }
      }
      
      // Update local state
      setHistory(prev => prev.filter(h => !batchSelectedItems.has(h.id)));
      setBatchSelectedItems(new Set());
      setIsBatchMode(false);
      
      toast.success(`Deleted ${successCount} items`, { id: toastId });
      return successCount;
    } catch (error) {
      console.error('Batch delete failed:', error);
      toast.error('Failed to delete items', { id: toastId });
      return 0;
    }
  }, [batchSelectedItems]);

  // Mark drafts as completed (remove is_draft flag)
  const markDraftsAsCompleted = useCallback(async (itemIds) => {
    const toastId = toast.loading('Marking as completed...');
    
    try {
      let successCount = 0;
      for (const itemId of itemIds) {
        try {
          await axios.patch(`${API_BASE}/history/${itemId}`, { is_draft: false }, { withCredentials: true });
          successCount++;
        } catch (e) {
          console.error(`Failed to mark ${itemId} as completed:`, e);
        }
      }
      
      // Update local state
      setHistory(prev => prev.map(h => 
        itemIds.includes(h.id) ? { ...h, is_draft: false } : h
      ));
      
      toast.success(`Marked ${successCount} items as completed`, { id: toastId });
      return successCount;
    } catch (error) {
      console.error('Mark as completed failed:', error);
      toast.error('Failed to mark items', { id: toastId });
      return 0;
    }
  }, []);

  // Update a single history item (e.g., mark as finished, update checklist)
  // Pass `_options.silent = true` to suppress the success toast — used by
  // the Submitted-to-Genesis checkbox flow which has its own visual feedback
  // (green SUBMITTED badge) and would otherwise spam the user with a toast
  // on every single click.
  const updateHistoryItem = useCallback(async (itemId, updates, _options = {}) => {
    const { silent = false } = _options;
    try {
      await axios.patch(`${API_BASE}/history/${itemId}`, updates, { withCredentials: true });
      setHistory(prev => prev.map(h => h.id === itemId ? { ...h, ...updates } : h));
      if (!silent) {
        toast.success(updates.status === 'finished' ? 'Marked as finished' : 'Updated');
      }
      return true;
    } catch (error) {
      console.error('Update history item failed:', error);
      if (!silent) toast.error('Update failed');
      return false;
    }
  }, []);

  // Bulk export selected history items as a single document
  const batchExportItems = useCallback(async (format = 'docx', letterhead = null) => {
    if (batchSelectedItems.size === 0) {
      toast.warning('No items selected');
      return;
    }

    const itemIds = Array.from(batchSelectedItems);
    const toastId = toast.loading(`Exporting ${itemIds.length} encounters as ${format.toUpperCase()}...`);

    try {
      const response = await axios.post(
        `${API_BASE}/export/bulk`,
        { item_ids: itemIds, format, letterhead },
        { withCredentials: true, responseType: 'arraybuffer' }
      );

      // Create download link
      const blob = new Blob([response.data], {
        type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.href = url;
      a.download = `WriteRight_Bulk_${itemIds.length}_Encounters_${today}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();

      toast.success(`Exported ${itemIds.length} encounters as ${format.toUpperCase()}!`, { id: toastId });
      exitBatchMode();
    } catch (error) {
      console.error('Bulk export failed:', error);
      let errorMsg = error.message;
      try {
        if (error.response?.data instanceof Blob) {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          errorMsg = parsed.detail || errorMsg;
        } else if (error.response?.data?.detail) {
          errorMsg = error.response.data.detail;
        }
      } catch (_) { console.warn("Error parsing export error details:", _?.message); }
      toast.error(`Export failed: ${errorMsg}`, { id: toastId });
    }
  }, [batchSelectedItems, exitBatchMode]);

  // Quick Export Today — one-click export of all today's encounters
  const quickExportToday = useCallback(async (format = 'docx', letterhead = null) => {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    const todayItems = history.filter(item => {
      const ts = new Date(item.timestamp);
      return ts >= todayStart;
    });

    if (todayItems.length === 0) {
      toast.info('No encounters from today to export');
      return;
    }

    const itemIds = todayItems.map(item => item.id).filter(Boolean);
    const toastId = toast.loading(`Exporting ${itemIds.length} today's encounters as ${format.toUpperCase()}...`);

    try {
      const response = await axios.post(
        `${API_BASE}/export/bulk`,
        { item_ids: itemIds, format, letterhead },
        { withCredentials: true, responseType: 'arraybuffer' }
      );

      const blob = new Blob([response.data], {
        type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.href = url;
      a.download = `WriteRight_Today_${itemIds.length}_Encounters_${today}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();

      toast.success(`Exported ${itemIds.length} today's encounters!`, { id: toastId });
    } catch (error) {
      console.error('Quick export failed:', error);
      let errorMsg = error.message;
      try {
        if (error.response?.data instanceof Blob) {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          errorMsg = parsed.detail || errorMsg;
        } else if (error.response?.data?.detail) {
          errorMsg = error.response.data.detail;
        }
      } catch (_) { console.warn("Error parsing export error details:", _?.message); }
      toast.error(`Export failed: ${errorMsg}`, { id: toastId });
    }
  }, [history]);

  // Export drafts input only (condensed, no duplicates)
  const quickExportDrafts = useCallback(async (format = 'docx') => {
    const drafts = history.filter(item => item.is_draft || (!item.clinical_note && !item.admin_letter && (item.notes || item.original_notes)));
    if (drafts.length === 0) {
      toast.info('No drafts to export');
      return;
    }
    const itemIds = drafts.map(item => item.id).filter(Boolean);
    const toastId = toast.loading(`Exporting ${itemIds.length} draft inputs as ${format.toUpperCase()}...`);
    try {
      const response = await axios.post(
        `${API_BASE}/export/bulk`,
        { item_ids: itemIds, format, drafts_only: true },
        { withCredentials: true, responseType: 'arraybuffer' }
      );
      const blob = new Blob([response.data], {
        type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `WriteRight_Drafts_${itemIds.length}_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success(`Exported ${itemIds.length} draft inputs!`, { id: toastId });
    } catch (error) {
      let errorMsg = error.message;
      try {
        if (error.response?.data instanceof Blob) {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          errorMsg = parsed.detail || errorMsg;
        } else if (error.response?.data?.detail) {
          errorMsg = error.response.data.detail;
        }
      } catch (_) { console.warn("Error parsing export error details:", _?.message); }
      toast.error(`Export failed: ${errorMsg}`, { id: toastId });
    }
  }, [history]);

  // Clear selected history item (for generating new entry)
  const clearSelectedHistoryItem = useCallback(() => {
    setSelectedHistoryItem(null);
  }, []);

  // Filtered history (memoized for performance)
  const filteredHistory = getFilteredHistory();

  return {
    // State
    showHistory,
    setShowHistory,
    history,
    setHistory,
    isLoadingHistory,
    selectedHistoryItem,
    setSelectedHistoryItem,
    batchSelectedItems,
    setBatchSelectedItems,
    isBatchMode,
    setIsBatchMode,
    historyFilter,
    setHistoryFilter,
    historySearchQuery,
    setHistorySearchQuery,
    condenseVersions,
    setCondenseVersions,
    expandedGroups,
    setExpandedGroups,
    dateJump,
    setDateJump,
    sortOrder,
    setSortOrder,
    showWeekendCatchup,
    setShowWeekendCatchup,
    showOrdersReview,
    setShowOrdersReview,
    historyShareCode,
    teamNotificationsEnabled,
    setTeamNotificationsEnabled,
    
    // Computed
    filteredHistory,
    
    // Actions
    loadHistory,
    getFilteredHistory,
    toggleBatchSelect,
    selectAllHistory,
    exitBatchMode,
    batchCopyNotesAndICD10s,
    toggleGroupExpanded,
    startShareHistory,
    getPendingProposals,
    getReadyForSignature,
    getPendingDraftsCount,
    getUnsubmittedCount,
    getSubmittedCount,
    clearSelectedHistoryItem,
    // New delete/mark functions
    deleteHistoryItem,
    batchDeleteItems,
    batchExportItems,
    quickExportToday,
    quickExportDrafts,
    markDraftsAsCompleted,
    updateHistoryItem
  };
};

export default useHistory;
